import Highcharts from 'highcharts/esm/highcharts.js';
import i18next from 'i18next';
import { ScoreColor } from '@components-clean/central-monitoring/panels/panel-constants';
import { HC_RAG_NAMES } from '@interfaces/opra-chart.interface';

// We need to set the height in both the chart and the container, otherwise we see the chart try and be bigger than it should be before
// transitioning to the specified height, which looks a bit odd.
export const CHART_HEIGHT_PIXELS = 317;

export const RAGLabel = (color: ScoreColor) => {
    switch (color) {
        case ScoreColor.Red:
            return i18next.t('Red');
        case ScoreColor.Amber:
            return i18next.t('Amber');
        case ScoreColor.Green:
            return i18next.t('Green');
        case ScoreColor.Purple:
            return i18next.t('Purple');
        case ScoreColor.None:
        default:
            break;
    }
};

export const RAGColourIndex = (color: string) => {
    switch (color) {
        case 'RED':
        case 'Red':
            return HC_RAG_NAMES.indexOf('Red');
        case 'AMBER':
        case 'Amber':
            return HC_RAG_NAMES.indexOf('Amber');
        case 'GREEN':
        case 'Green':
            return HC_RAG_NAMES.indexOf('Green');
        case 'PURPLE':
        case 'Purple':
            return HC_RAG_NAMES.indexOf('Purple');
        case '':
        default:
            return HC_RAG_NAMES.indexOf('None');
    }
};

export const getCreditsStyle = (mainPanel: string): Highcharts.CreditsOptions => {
    return {
        enabled: true,
        text: mainPanel,
        position: {
            x: -10,
            y: -130,
            align: 'right',
            verticalAlign: 'middle',
        },
        style: {
            fontSize: '33px',
            color: 'primary',
            opacity: 0.12,
            cursor: 'default',
            pointerEvents: 'none',
        },
        href: '#',
    };
};

export const getChartEvents = (
    showWaterMark: boolean,
    showSubtitle: boolean,
    title?: string,
    subTitle?: string,
    eventOptions?: {
        fullScreenOptions?: Highcharts.Options;
        printOptions?: Highcharts.Options;
        defaultOptions?: Highcharts.Options;
    },
): Highcharts.ChartEventsOptions => {
    return {
        beforePrint() {
            const self: Highcharts.Chart = this as unknown as Highcharts.Chart;

            self.update(
                Highcharts.merge(
                    {
                        credits: {
                            enabled: false,
                        },
                        chart: {
                            animation: false,
                        },
                        title: {
                            text: title,
                        },
                        subtitle:
                            !showSubtitle && subTitle
                                ? {
                                      text: subTitle,
                                      useHTML: true,
                                      verticalAlign: 'bottom',
                                      align: 'center',
                                  }
                                : undefined,
                    },
                    eventOptions?.printOptions,
                ),
            );
        },
        afterPrint() {
            const self: Highcharts.Chart = this as unknown as Highcharts.Chart;

            self.update(
                Highcharts.merge(
                    {
                        credits: {
                            enabled: showWaterMark,
                        },
                        title: {
                            text: undefined,
                        },
                        subtitle:
                            !showSubtitle && subTitle
                                ? {
                                      text: undefined,
                                  }
                                : undefined,
                    },
                    eventOptions?.defaultOptions,
                ),
            );

            // Set the animations after the above redraw, but set this update's redraw to false
            // This makes for a more seemless transition from fullscreen to small without animations,
            // and then re-enables animations
            self.update(
                {
                    chart: {
                        animation: true,
                    },
                },
                false,
            );
        },
        fullscreenOpen() {
            const self: Highcharts.Chart = this as unknown as Highcharts.Chart;

            self.update(
                Highcharts.merge(
                    {
                        credits: {
                            enabled: false,
                        },
                        title: {
                            text: title,
                        },
                        subtitle:
                            !showSubtitle && subTitle
                                ? {
                                      text: subTitle,
                                      useHTML: true,
                                      verticalAlign: 'bottom',
                                      align: 'center',
                                  }
                                : undefined,
                    },
                    eventOptions?.fullScreenOptions,
                ),
                false,
            );
        },
        fullscreenClose() {
            const self: Highcharts.Chart = this as unknown as Highcharts.Chart;

            let defaults = eventOptions?.defaultOptions;
            // Remove any height/width properties that might be in defaultOptions as this can cause performance issues
            if (eventOptions?.defaultOptions?.chart) {
                defaults = {
                    ...eventOptions.defaultOptions,
                    chart: {
                        ...eventOptions.defaultOptions.chart,
                        height: undefined,
                        width: undefined,
                    },
                };
            }

            self.update(
                Highcharts.merge(
                    {
                        credits: {
                            enabled: showWaterMark,
                        },
                        title: {
                            text: undefined,
                        },
                        subtitle:
                            !showSubtitle && subTitle
                                ? {
                                      text: undefined,
                                  }
                                : undefined,
                    },
                    defaults,
                ),
                false,
            );
        },
    };
};
